import { useEffect, useState } from 'react'

// @ts-ignore
import { getData as getCountries } from 'country-list'
// @ts-ignore
import currencies from 'currency-codes/data'
import CheckIcon from 'mdi-react/CheckIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  API_ROUTE_CREDIT_REQUEST_QUOTE_CUSTOMERS,
  API_ROUTE_CREDIT_REQUEST_QUOTES,
} from '@/api/apiRoutes'
import Page from '@/components/content/Page'
import Button from '@/components/form/Button'
import Input from '@/components/form/Input'
import ReactSelect from '@/components/form/ReactSelect'
import { Translation } from '@/components/i18n/Translation'
import ButtonLink from '@/components/util/ButtonLink'
import Highlight from '@/components/util/Highlight'
import LoaderCircle from '@/components/util/LoaderCircle'
import OnKeyDownCatcher from '@/components/util/OnKeyDownCatcher'
import { activatePopup } from '@/ducks/popupReducer'
import { useApiCallerGET, useApiCallerPOST } from '@/hooks/useApiCaller2'
import { applyTextFilter, appRoutes } from '@/util/misc'

import './credit-request-new-quote-page.scss'

// @ts-ignore
const stringMatcher = c =>
  `${c.customer_name} ${c.customer_address_line1} ${c.customer_address_line2} ${c.customer_address_city} ${c.customer_address_zip} ${c.customer_org_no}`
const countries = getCountries()

const CreditRequestNewQuotePage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [customers, setCustomers] = useState([])

  const [customerName, setCustomerName] = useState('')
  const [customerRegistrationNumber, setCustomerRegistrationNumber] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [zip, setZip] = useState('')
  const [country, setCountry] = useState('')
  const [leaseDuration, setLeaseDuration] = useState(36)
  const [value, setValue] = useState('')
  const [currency, setCurrency] = useState('EUR')

  const [isCustomerPickerVisible, setIsCustomerPickerVisible] = useState(false)
  const [customerSelectionIndex, setCustomerSelectionIndex] = useState(0)
  const customersFiltered = applyTextFilter(customers, customerName, stringMatcher)
  const customersSliced = customersFiltered.slice(0, 5)
  // @ts-ignore
  const { callApi: getCustomersApi } = useApiCallerGET(API_ROUTE_CREDIT_REQUEST_QUOTE_CUSTOMERS)
  // @ts-ignore
  const { callApi: postRequestApi, isLoading } = useApiCallerPOST(API_ROUTE_CREDIT_REQUEST_QUOTES, {
    customerName,
    customerRegistrationNumber,
    address,
    city,
    zip,
    country,
    leaseDuration,
    value,
    currency,
  })

  useEffect(() => {
    // @ts-ignore
    getCustomersApi().then(customers => setCustomers(customers))
  }, [])

  useEffect(() => {
    setCustomerSelectionIndex(0)
  }, [customerName, customers.length])

  const increaseCustomerSelectionIndex = () => {
    customerSelectionIndex < customersSliced.length - 1 &&
      setCustomerSelectionIndex(customerSelectionIndex + 1)
  }

  const decreaseCustomerSelectionIndex = () => {
    customerSelectionIndex > 0 && setCustomerSelectionIndex(customerSelectionIndex - 1)
  }

  const onSelect = (idx: any) => {
    const {
      customer_name,
      customer_address_line1,
      customer_address_city,
      customer_address_zip,
      customer_org_no,
      country_code,
    } = customersFiltered[idx] || {}
    setIsCustomerPickerVisible(false)
    setCustomerName(customer_name)
    setCustomerRegistrationNumber(customer_org_no)
    setAddress(customer_address_line1)
    setCity(customer_address_city)
    setZip(customer_address_zip)
    setCountry(country_code)
  }
  // @ts-ignore
  const currencyOptions = currencies.map(currency => ({
    value: currency.code,
    label: `${currency.currency} (${currency.code})`,
  }))
  // @ts-ignore
  const countryOptions = countries.map(country => ({
    value: country.code,
    label: `${country.name}`,
  }))

  const submitCreditRequest = async () => {
    if (
      !customerName ||
      !customerRegistrationNumber ||
      !address ||
      !zip ||
      !city ||
      !country ||
      !leaseDuration ||
      !value ||
      !currencies
    ) {
      dispatch(
        activatePopup(
          <div>
            <h3>Error</h3>
            <p>All fields required to perform a credit request.</p>
          </div>
        )
      )
      return
    }

    try {
      await postRequestApi()
      navigate(appRoutes.creditRequest)
    } catch {}
  }

  if (isLoading) {
    return (
      <Page className="credit-request-new-quote-page">
        <h1>
          <Translation id="New credit request" />
        </h1>
        <LoaderCircle />
      </Page>
    )
  }

  return (
    <Page className="credit-request-new-quote-page">
      <h1>
        <Translation id="New credit request" />
      </h1>

      <div className="credit-request-new-quote-page__form">
        <section>
          <h3>Customer</h3>

          <OnKeyDownCatcher
            onEsc={() => setIsCustomerPickerVisible(false)}
            onDown={increaseCustomerSelectionIndex}
            onUp={decreaseCustomerSelectionIndex}
            onEnter={() => onSelect(customerSelectionIndex)}
          >
            <Input
              label="Name"
              onChange={e => setCustomerName(e.target.value)}
              onFocus={() => setIsCustomerPickerVisible(true)}
              onClick={() => setIsCustomerPickerVisible(true)}
              onBlur={() => setIsCustomerPickerVisible(false)}
              value={customerName}
            />
          </OnKeyDownCatcher>

          <Input
            label="Customer registration number"
            onChange={e => setCustomerRegistrationNumber(e.target.value)}
            value={customerRegistrationNumber}
          />

          <Input label="Address" onChange={e => setAddress(e.target.value)} value={address} />

          <Input
            label="Zip/Postal"
            onChange={e => setZip(e.target.value)}
            value={zip}
            maxLength={10}
          />

          <Input label="City" onChange={e => setCity(e.target.value)} value={city} />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>Country</label>

          <ReactSelect
            options={countryOptions}
            value={country}
            // @ts-ignore
            onChange={value => setCountry(value)}
          />

          <div
            className={`customer-suggestion ${
              isCustomerPickerVisible && customersSliced.length > 0 ? '' : 'is-hidden'
            }`}
          >
            {customersSliced.length === 0 && 'No sugestions...'}
            {/* @ts-ignore */}
            {customersSliced.map((customer, idx) => (
              <Customer
                key={idx}
                isSelected={idx === customerSelectionIndex}
                query={customerName}
                onClick={() => onSelect(idx)}
                onMouseOver={() => setCustomerSelectionIndex(idx)}
                {...customer}
              />
            ))}
          </div>
        </section>

        <section>
          <h3>Quote</h3>

          <Input
            label="Lease duration (months)"
            // @ts-ignore
            onChange={e => setLeaseDuration(e.target.value)}
            value={leaseDuration}
            type="number"
          />

          <Input
            label="Value"
            onChange={e => setValue(e.target.value)}
            value={value}
            type="number"
          />

          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>Currency</label>

          <ReactSelect
            options={currencyOptions}
            value={currency}
            // @ts-ignore
            onChange={value => setCurrency(value)}
          />

          <div className="buttons">
            <ButtonLink to={appRoutes.creditRequest}>
              <CloseIcon />
              Cancel
            </ButtonLink>

            <Button onClick={submitCreditRequest} Icon={CheckIcon}>
              Submit
            </Button>
          </div>
        </section>
      </div>
    </Page>
  )
}

const Customer = ({
  customer_name,
  customer_address_line1,
  customer_address_line2,
  customer_address_city,
  customer_address_zip,
  customer_org_no,
  query,
  isSelected,
  onClick,
  onMouseOver,
}: any) => (
  // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
  <button onClick={onClick} onMouseOver={onMouseOver} className={isSelected ? 'is-selected' : ''}>
    <div className="customer-name">
      <Highlight text={customer_name} pattern={query} />
    </div>

    <div className="customer-org-no">
      <Highlight text={customer_org_no} pattern={query} />
    </div>

    <address className="customer-address">
      <div className="customer-address__line1">
        <Highlight text={customer_address_line1} pattern={query} />
      </div>

      {customer_address_line2 && (
        <div className="customer-address__line2">
          <Highlight text={customer_address_line2} pattern={query} />
        </div>
      )}

      <div className="customer-address__zip-city">
        <Highlight text={customer_address_zip} pattern={query} />
        , <Highlight text={customer_address_city} pattern={query} />
      </div>
    </address>
  </button>
)

export default CreditRequestNewQuotePage
